import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { ArrowLeft, Camera } from "lucide-react";
import ImageDosa from "../../assets/food.png";
import welcomeBg from "../../assets/homebg.png";

import { useFoodManagement } from "../../hooks/useFoodManagement";
import {
  FoodItem as ApiFoodItem,
  foodApiService,
  EditFoodItemPayload,
  SaveMealPayload,
} from "../../services/foodApiService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import NutritionFactsCard from "../../components/common/NutritionFactsCard";
import FoodItemsList from "./components/FoodItemsList";

interface FoodItem {
  id: number;
  food_item_id?: string; // API food item ID
  name: string;
  csv_name?: string; // Backend field: normalized name
  source?: string; // Backend field: llm, csv, etc.
  count?: number; // Backend field: item count
  calories: number;
  calorie_content_kcal?: number; // API field for calories
  weight: number;
  servings: number;
  image: string;
  // API nutrition data
  carbohydrate_content_g?: number;
  protein_content_g?: number;
  fat_content_g?: number;
  dietary_fiber_g?: number;
  net_carbs_g?: number;
  sugar_g?: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  GI_estimate?: number;
  GL_estimate?: number;
  serving_description?: string;
}

interface NutritionTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

const ScannedFooddetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uploadedImage, foodItemsFromBackend, documentId } =
    useFoodManagement();

  // Get meal number from navigation state
  const mealNumber = location.state?.mealNumber ?? 1;
  const fromHomePage = location.state?.fromHomePage ?? false;

  // Handle back navigation - Always go to home page for complete flow
  const handleBackNavigation = () => {
    // Always navigate to home page for complete flow
    navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`, {
      state: {
        mealNumber: mealNumber,
        fromScannedDetail: true,
      },
    });
  };

  // State for uploaded image with localStorage persistence
  const [currentUploadedImage, setCurrentUploadedImage] = useState<
    string | null
  >(() => {
    // Try to load image from localStorage first
    const savedImage = localStorage.getItem("glucera_uploaded_image");

    // Prefer saved base64 data URL over temporary blob URL
    if (savedImage?.startsWith("data:")) {
      return savedImage;
    } else if (savedImage?.startsWith("blob:")) {
      // Check if blob URL is still valid (though this is unlikely to work after page refresh)
      return savedImage;
    } else if (savedImage?.startsWith("http")) {
      return savedImage;
    } else if (uploadedImage) {
      return uploadedImage;
    }

    return null;
  });

  // Convert API food items to local format
  const convertApiFoodItems = useCallback(
    (apiItems: ApiFoodItem[]): FoodItem[] => {
      return apiItems.map((item, index) => ({
        id: index + 1,
        food_item_id: (item as any).food_item_id, // API items may have food_item_id
        name: item.name,
        csv_name: (item as any).csv_name, // Backend field
        source: (item as any).source, // Backend field
        count: (item as any).count ?? 1, // Backend field
        calories: item.calorie_content_kcal,
        weight: item.serving_size_g,
        servings: 1,
        image: currentUploadedImage ?? uploadedImage ?? ImageDosa,
        carbohydrate_content_g: item.carbohydrate_content_g,
        protein_content_g: item.protein_content_g,
        fat_content_g: item.fat_content_g,
        dietary_fiber_g: item.dietary_fiber_g,
        net_carbs_g: item.net_carbs_g,
        sugar_g: (item as any).sugar_g ?? 0,
        trans_fat_g: item.trans_fat_g ?? 0,
        saturated_fat_g: item.saturated_fat_g ?? 0,
        GI_estimate: item.GI_estimate,
        GL_estimate: item.GL_estimate,
        serving_description: item.serving_description,
      }));
    },
    [currentUploadedImage, uploadedImage]
  );

  const [foodItems, setFoodItems] = useState<FoodItem[]>(() => {
    // ALWAYS prefer fresh API data over localStorage
    if (foodItemsFromBackend && foodItemsFromBackend.length > 0) {
      const convertedItems = convertApiFoodItems(foodItemsFromBackend);
      return convertedItems;
    }

    // Fallback to localStorage only if no API data
    const savedItems = localStorage.getItem("glucera_food_items");
    if (savedItems) {
      try {
        const parsedItems = JSON.parse(savedItems);

        // Update food items with current saved image
        const savedImage = localStorage.getItem("glucera_uploaded_image");

        if (savedImage && parsedItems.length > 0) {
          const updatedItems = parsedItems.map((item: FoodItem) => ({
            ...item,
            image: savedImage,
          }));
          return updatedItems;
        }

        return parsedItems;
      } catch (_error) {
        // Error parsing saved items
      }
    }

    // Return empty array if no API data
    return [];
  });

  const [selectedFoodItem, setSelectedFoodItem] = useState<FoodItem | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [nutritionTotals, setNutritionTotals] =
    useState<NutritionTotals | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [mealType, setMealType] = useState<string>("Breakfast"); // Django expects: Breakfast, Lunch, Dinner, Snack, Other

  // NOTE: mealId removed as add API is no longer used

  // On page load, ensure food items have correct images
  useEffect(() => {
    const savedImage = localStorage.getItem("glucera_uploaded_image");
    const savedItems = localStorage.getItem("glucera_food_items");

    // If we have saved image but food items don't have it, update them
    if (savedImage && savedItems) {
      try {
        const parsedItems = JSON.parse(savedItems);
        const needsUpdate = parsedItems.some(
          (item: FoodItem) => item.image !== savedImage
        );

        if (needsUpdate) {
          const updatedItems = parsedItems.map((item: FoodItem) => ({
            ...item,
            image: savedImage,
          }));
          setFoodItems(updatedItems);
          try {
            // Store only last 50 items to prevent quota issues
            const MAX_ITEMS = 50;
            const limitedItems = updatedItems.slice(0, MAX_ITEMS);
            localStorage.setItem(
              "glucera_food_items",
              JSON.stringify(limitedItems)
            );
          } catch (storageError) {
            // Quota exceeded - clear old data and retry
            console.warn("localStorage quota exceeded, clearing old data");
            localStorage.removeItem("glucera_food_items");
          }
        }
      } catch (error) {
        // Error parsing saved items on page load
      }
    }
  }, []); // Run only once on component mount

  // Track documentId changes
  useEffect(() => {
    // Document ID tracking
  }, [documentId]);

  // Convert blob URL to base64 and save to localStorage
  useEffect(() => {
    if (uploadedImage?.startsWith("blob:")) {
      // Convert blob URL to base64 data URL
      fetch(uploadedImage)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            localStorage.setItem("glucera_uploaded_image", base64String);
            setCurrentUploadedImage(base64String);
          };
          reader.onerror = _error => {
            // Fallback - save the blob URL anyway
            localStorage.setItem("glucera_uploaded_image", uploadedImage);
            setCurrentUploadedImage(uploadedImage);
          };
          reader.readAsDataURL(blob);
        })
        .catch(_error => {
          // Fallback - save the blob URL anyway (though it may not work after refresh)
          localStorage.setItem("glucera_uploaded_image", uploadedImage);
          setCurrentUploadedImage(uploadedImage);
        });
    } else if (uploadedImage) {
      // If it's already a data URL or other format, save directly
      localStorage.setItem("glucera_uploaded_image", uploadedImage);
      setCurrentUploadedImage(uploadedImage);
    }
  }, [uploadedImage]);

  // Force update food items when currentUploadedImage changes
  useEffect(() => {
    const imageToUse = currentUploadedImage ?? uploadedImage;
    if (imageToUse && foodItems.length > 0) {
      setFoodItems(prevItems =>
        prevItems.map(item => ({
          ...item,
          image: imageToUse,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUploadedImage, uploadedImage]);

  // Update food items when API data changes
  useEffect(() => {
    if (foodItemsFromBackend && foodItemsFromBackend.length > 0) {
      // Always prefer fresh API data over localStorage when new API data comes
      const convertedItems = convertApiFoodItems(foodItemsFromBackend);

      setFoodItems(convertedItems);
      setSelectedFoodItem(convertedItems[0] ?? null);

      // Save to localStorage for persistence
      try {
        // Store only last 50 items to prevent quota issues
        const MAX_ITEMS = 50;
        const limitedItems = convertedItems.slice(0, MAX_ITEMS);
        localStorage.setItem(
          "glucera_food_items",
          JSON.stringify(limitedItems)
        );
      } catch (storageError) {
        // Quota exceeded - clear and skip saving
        console.warn("localStorage quota exceeded, skipping save");
        localStorage.removeItem("glucera_food_items");
      }
    }
  }, [foodItemsFromBackend, convertApiFoodItems]);

  // Generate unique ID for new items
  const generateNewId = useCallback(() => {
    const maxId = Math.max(...foodItems.map(item => item.id), 0);
    return maxId + 1;
  }, [foodItems]);

  // Calculate nutrition totals
  const calculateTotals = useCallback(() => {
    if (foodItems.length === 0) {
      setNutritionTotals(null);
      return;
    }

    const totals = foodItems.reduce(
      (acc, item) => ({
        carbohydrate_content_g:
          acc.carbohydrate_content_g + (item.carbohydrate_content_g ?? 0),
        dietary_fiber_g: acc.dietary_fiber_g + (item.dietary_fiber_g ?? 0),
        net_carbs_g: acc.net_carbs_g + (item.net_carbs_g ?? 0),
        sugar_g: acc.sugar_g + (item.sugar_g ?? 0),
        protein_content_g:
          acc.protein_content_g + (item.protein_content_g ?? 0),
        fat_content_g: acc.fat_content_g + (item.fat_content_g ?? 0),
        trans_fat_g: acc.trans_fat_g + (item.trans_fat_g ?? 0),
        saturated_fat_g: acc.saturated_fat_g + (item.saturated_fat_g ?? 0),
        calorie_content_kcal:
          acc.calorie_content_kcal +
          (item.calorie_content_kcal || item.calories || 0),
        GI_estimate: acc.GI_estimate + (item.GI_estimate ?? 0),
        GL_estimate:
          acc.GL_estimate +
          (item.GL_estimate ??
            ((item.GI_estimate ?? 0) * (item.net_carbs_g ?? 0)) / 100),
      }),
      {
        carbohydrate_content_g: 0,
        dietary_fiber_g: 0,
        net_carbs_g: 0,
        sugar_g: 0,
        protein_content_g: 0,
        fat_content_g: 0,
        trans_fat_g: 0,
        saturated_fat_g: 0,
        calorie_content_kcal: 0,
        GI_estimate: 0,
        GL_estimate: 0,
      }
    );

    // GI and GL are both simple sums
    setNutritionTotals({
      ...totals,
      GI_estimate: totals.GI_estimate,
      GL_estimate: totals.GL_estimate,
    });
  }, [foodItems]);

  // Update totals when food items change
  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Track food items changes for debugging
  useEffect(() => {
    // Save to localStorage whenever items change
    if (foodItems.length > 0) {
      // Ensure all items have the current uploaded image
      const itemsWithCurrentImage = foodItems.map(item => ({
        ...item,
        image: currentUploadedImage ?? item.image,
      }));

      try {
        // Store only last 50 items to prevent quota issues
        const MAX_ITEMS = 50;
        const limitedItems = itemsWithCurrentImage.slice(0, MAX_ITEMS);
        localStorage.setItem(
          "glucera_food_items",
          JSON.stringify(limitedItems)
        );
      } catch (storageError) {
        // Quota exceeded - clear old data
        console.warn("localStorage quota exceeded");
        localStorage.removeItem("glucera_food_items");
      }
    }
  }, [foodItems, currentUploadedImage]);

  // Toggle accordion item
  const toggleExpanded = (itemId: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // NOTE: Add API function removed as per requirement - all adds are local only
  // Helper function to add new food item via API - DISABLED
  /*
  const addFoodItemToApi = useCallback(
    async (newItem: FoodItem) => {
      // This function is no longer used - add operations are local only
      message.info("Add functionality is local only - no API calls made");
    },
    [mealId, uploadedImage]
  );
  */

  // Helper function to edit food item via API
  const editFoodItemInApi = useCallback(async (updatedItem: FoodItem) => {
    if (!updatedItem.food_item_id) {
      return;
    }

    try {
      const payload: EditFoodItemPayload = {
        name: updatedItem.name,
        serving_size_g: updatedItem.weight,
        serving_description:
          updatedItem.serving_description ?? `${updatedItem.weight}g serving`,
      };

      const response = await foodApiService.editFoodItem(
        updatedItem.food_item_id,
        payload
      );

      // Update the specific item in local state
      setFoodItems(prevItems =>
        prevItems.map(item =>
          item.food_item_id === updatedItem.food_item_id
            ? {
                ...item,
                name: response.data.food_items[0].name,
                calories: response.data.food_items[0].calorie_content_kcal,
                weight: response.data.food_items[0].serving_size_g,
                carbohydrate_content_g:
                  response.data.food_items[0].carbohydrate_content_g,
                protein_content_g:
                  response.data.food_items[0].protein_content_g,
                fat_content_g: response.data.food_items[0].fat_content_g,
                dietary_fiber_g: response.data.food_items[0].dietary_fiber_g,
                net_carbs_g: response.data.food_items[0].net_carbs_g,
                sugar_g: response.data.food_items[0].sugar_g,
                trans_fat_g: response.data.food_items[0].trans_fat_g,
                saturated_fat_g: response.data.food_items[0].saturated_fat_g,
                GI_estimate: response.data.food_items[0].GI_estimate,
                GL_estimate: response.data.food_items[0].GL_estimate,
                serving_description:
                  response.data.food_items[0].serving_description,
              }
            : item
        )
      );

      // Update selected item if it's the one being edited
      setSelectedFoodItem(prevSelectedItem => {
        if (prevSelectedItem?.food_item_id === updatedItem.food_item_id) {
          return {
            ...prevSelectedItem,
            name: response.data.food_items[0].name,
            calories: response.data.food_items[0].calorie_content_kcal,
            weight: response.data.food_items[0].serving_size_g,
            carbohydrate_content_g:
              response.data.food_items[0].carbohydrate_content_g,
            protein_content_g: response.data.food_items[0].protein_content_g,
            fat_content_g: response.data.food_items[0].fat_content_g,
            dietary_fiber_g: response.data.food_items[0].dietary_fiber_g,
            net_carbs_g: response.data.food_items[0].net_carbs_g,
            sugar_g: response.data.food_items[0].sugar_g,
            trans_fat_g: response.data.food_items[0].trans_fat_g,
            saturated_fat_g: response.data.food_items[0].saturated_fat_g,
            GI_estimate: response.data.food_items[0].GI_estimate,
            GL_estimate: response.data.food_items[0].GL_estimate,
            serving_description:
              response.data.food_items[0].serving_description,
          } as FoodItem;
        }
        return prevSelectedItem;
      });

      message.success(response.data.message);
    } catch (_error) {
      message.error("Failed to edit food item. Please try again.");
    }
  }, []);

  const handleEditItem = (item: FoodItem) => {
    navigate(`/glucera${GluceraNavigationRoutes.EDIT_FOOD}`, {
      state: {
        foodItem: {
          ...item,
          image: currentUploadedImage ?? uploadedImage ?? item.image,
        },
        returnPath: `/glucera${GluceraNavigationRoutes.SCANNED_FOOD_DETAIL}`,
        mealNumber: mealNumber,
        fromHomePage: true, // Always treat as from home for complete flow
        completeFlow: true, // Flag to indicate this is part of complete flow
      },
    });
  };

  const handleDeleteItem = async (itemId: number) => {
    const itemToDelete = foodItems.find(item => item.id === itemId);

    if (!itemToDelete?.food_item_id) {
      // If no API ID, just remove from local state (for locally added items)
      setFoodItems(foodItems.filter(item => item.id !== itemId));
      if (selectedFoodItem?.id === itemId) {
        setSelectedFoodItem(foodItems.find(item => item.id !== itemId) ?? null);
      }
      return;
    }

    try {
      // Call API to delete the item
      const response = await foodApiService.deleteFoodItem(
        itemToDelete.food_item_id
      );

      // Update local state with API response
      const updatedItems = response.data.food_items.map((apiItem, index) => ({
        id: index + 1,
        food_item_id: apiItem.food_item_id,
        name: apiItem.name,
        calories: apiItem.calorie_content_kcal,
        weight: apiItem.serving_size_g,
        servings: 1,
        image: currentUploadedImage ?? uploadedImage ?? ImageDosa,
        carbohydrate_content_g: apiItem.carbohydrate_content_g,
        protein_content_g: apiItem.protein_content_g,
        fat_content_g: apiItem.fat_content_g,
        dietary_fiber_g: apiItem.dietary_fiber_g,
        net_carbs_g: apiItem.net_carbs_g,
        GI_estimate: apiItem.GI_estimate,
        GL_estimate: apiItem.GL_estimate,
        serving_description: apiItem.serving_description,
      }));

      setFoodItems(updatedItems);

      // Update selected item
      if (selectedFoodItem?.id === itemId) {
        setSelectedFoodItem(updatedItems[0] ?? null);
      }

      message.success(response.data.message);
    } catch (_error) {
      message.error("Failed to delete food item. Please try again.");
    }
  };

  // State to track processed navigation events to prevent infinite loops
  const [processedLocationKey, setProcessedLocationKey] = useState<
    string | null
  >(null);

  // Handle updated item from edit page and new item from add page
  useEffect(() => {
    // Prevent processing same location state multiple times
    if (processedLocationKey === location.key) {
      return;
    }

    const updatedItem = location.state?.updatedItem;
    const newItem = location.state?.newItem;
    const localUpdatedItem = location.state?.localUpdatedItem;

    // Only process if we have some data to work with
    if (!updatedItem && !newItem && !localUpdatedItem) {
      return;
    }

    // Mark this location as processed
    setProcessedLocationKey(location.key);

    // Handle local item updates (no API call needed)
    if (localUpdatedItem) {
      setFoodItems(prev =>
        prev.map(item =>
          item.id === localUpdatedItem.id
            ? { ...item, ...localUpdatedItem }
            : item
        )
      );

      // Update selected item if it's the one being edited
      setSelectedFoodItem(prev => {
        if (prev?.id === localUpdatedItem.id) {
          return { ...prev, ...localUpdatedItem };
        }
        return prev;
      });

      // message.success("Food item updated successfully!"); // Removed: Show message only when saving meal

      // Clear the state after a delay
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 500);
    }

    // Handle API item updates
    if (updatedItem) {
      // Call API to update the item
      editFoodItemInApi(updatedItem);

      // Clear the state after a delay
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 500);
    }

    // Handle new item addition - Always local, no API calls
    if (newItem) {
      // Always add locally without API integration
      const updatedItems = [...foodItems, newItem];

      setFoodItems(updatedItems);
      setSelectedFoodItem(newItem);
      // message.success(`Added "${newItem.name}" successfully!`); // Removed: Show message only when saving meal

      // Clear the state after a delay
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 500);
    }
  }, [
    location.key,
    location.state,
    processedLocationKey,
    editFoodItemInApi,
    foodItems,
  ]);

  const handleAddItem = () => {
    navigate(`/glucera${GluceraNavigationRoutes.ADD_FOOD}`, {
      state: {
        returnPath: `/glucera${GluceraNavigationRoutes.SCANNED_FOOD_DETAIL}`,
        mealNumber: mealNumber,
        fromHomePage: true, // Always treat as from home for complete flow
        completeFlow: true, // Flag to indicate this is part of complete flow
        // Pass current food items count for ID generation
        nextItemId: generateNewId(),
        // Pass uploaded image for consistency
        uploadedImage: uploadedImage,
        // Pass current items for context
        currentFoodItems: foodItems,
      },
    });
  };

  const handleSave = async () => {
    // Validate if we have food items to save
    if (foodItems.length === 0) {
      message.error("No food items to save!");
      return;
    }

    // Validate if we have nutritionTotals
    if (!nutritionTotals) {
      message.error("Nutrition totals not calculated!");
      return;
    }

    // Handle document_id - required for API
    let finalDocumentId: string;

    if (!documentId) {
      // Check if we have any items with food_item_id (from scanning)
      const hasScannedItems = foodItems.some(item => item.food_item_id);

      if (hasScannedItems) {
        // If we have scanned items but no documentId, it's an error
        message.error(
          "Document ID missing for scanned items! Please try scanning again."
        );
        return;
      } else {
        // All items are manually added - generate UUID for local storage only
        finalDocumentId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          }
        );

        // For manual entries, save locally and skip API call
        message.success({
          content: `Saved ${foodItems.length} item${foodItems.length !== 1 ? "s" : ""} locally!`,
          duration: 3,
        });

        // Save locally
        localStorage.setItem(
          `glucera_manual_meal_${Date.now()}`,
          JSON.stringify({
            foodItems,
            nutritionTotals,
            timestamp: new Date().toISOString(),
            documentId: finalDocumentId,
          })
        );

        // Clear temporary localStorage
        localStorage.removeItem("glucera_food_items");
        localStorage.removeItem("glucera_uploaded_image");
        localStorage.removeItem("glucera_document_id"); // Clear document ID for manual entries too

        // Navigate to meal page
        setTimeout(() => {
          setIsSaving(false);
          navigate(`/glucera${GluceraNavigationRoutes.Mealpage}`, {
            state: {
              message: "Food items saved locally!",
              fromSave: true,
              savedItemsCount: foodItems.length,
              isLocalSave: true,
            },
          });
        }, 1500);

        return; // Exit early for manual entries
      }
    } else {
      // Use the existing document ID from scanning
      finalDocumentId = documentId;
    }

    // Set saving state
    setIsSaving(true);

    try {
      // Prepare payload for save meal API
      // First, try to get full API response from localStorage
      const storedResponse = localStorage.getItem("glucera_analyze_response");
      let dateString = localStorage.getItem("glucera_meal_date");
      
      console.log("🔍 SAVE MEAL - Checking localStorage:");
      console.log("📦 Stored analyze response:", storedResponse ? "Found" : "Not found");
      console.log("📅 Date from localStorage:", dateString);
      console.log("📦 All localStorage keys:", Object.keys(localStorage));
      
      // If we have stored response, extract date from it
      if (storedResponse && !dateString) {
        try {
          const parsedResponse = JSON.parse(storedResponse);
          dateString = parsedResponse.date;
          console.log("📅 Date extracted from stored response:", dateString);
        } catch (e) {
          console.error("❌ Error parsing stored response:", e);
        }
      }

      // If no date in localStorage, create date string manually
      if (!dateString) {
        console.warn("⚠️ No date found in localStorage, generating current UTC time");
        // Create date string in format: "2025-11-05 17:47:08" using UTC time
        // Backend expects UTC time, so we send UTC to match what backend returns
        const currentDate = new Date();

        // Format: YYYY-MM-DD HH:mm:ss using UTC time (to match backend)
        const year = currentDate.getUTCFullYear();
        const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
        const day = String(currentDate.getUTCDate()).padStart(2, "0");
        const hours = String(currentDate.getUTCHours()).padStart(2, "0");
        const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
        const seconds = String(currentDate.getUTCSeconds()).padStart(2, "0");

        dateString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        console.log("🕐 Generated current date:", dateString);
      } else {
        console.log("✅ Using date from localStorage:", dateString);
      }

      const payload: SaveMealPayload = {
        document_id: finalDocumentId,
        date: dateString, // Format: "2025-11-05 17:47:08"
        meal_type: mealType, // Django expects: Breakfast, Lunch, Dinner, Snack, Other
        food_items: foodItems.map(item => {
          return {
            name: item.name,
            csv_name:
              item.csv_name ?? item.name.toLowerCase().replace(/\s+/g, "_"), // Backend field
            source: item.source ?? "llm", // Backend field: llm, csv, etc.
            count: item.count ?? 1, // Backend field: item count
            serving_size_g: Number(item.weight), // Ensure it's a number
            serving_description:
              item.serving_description ?? `${item.weight}g serving`,
            carbohydrate_content_g: Number(item.carbohydrate_content_g ?? 0),
            dietary_fiber_g: Number(item.dietary_fiber_g ?? 0),
            net_carbs_g: Number(item.net_carbs_g ?? 0),
            sugar_g: Number(item.sugar_g ?? 0),
            protein_content_g: Number(item.protein_content_g ?? 0),
            fat_content_g: Number(item.fat_content_g ?? 0),
            trans_fat_g: Number(item.trans_fat_g ?? 0),
            saturated_fat_g: Number(item.saturated_fat_g ?? 0),
            calorie_content_kcal: Number(item.calories),
            GI_estimate: Number(item.GI_estimate ?? 0),
            GL_estimate: Number(item.GL_estimate ?? 0),
          };
        }),
        totals: {
          carbohydrate_content_g: Number(
            nutritionTotals.carbohydrate_content_g
          ),
          dietary_fiber_g: Number(nutritionTotals.dietary_fiber_g),
          net_carbs_g: Number(nutritionTotals.net_carbs_g),
          sugar_g: Number(nutritionTotals.sugar_g ?? 0),
          protein_content_g: Number(nutritionTotals.protein_content_g),
          fat_content_g: Number(nutritionTotals.fat_content_g),
          trans_fat_g: Number(nutritionTotals.trans_fat_g ?? 0),
          saturated_fat_g: Number(nutritionTotals.saturated_fat_g ?? 0),
          calorie_content_kcal: Number(nutritionTotals.calorie_content_kcal),
          GI_estimate: Number(nutritionTotals.GI_estimate),
          GL_estimate: Number(nutritionTotals.GL_estimate),
        },
      };

      console.log("📤 SAVE MEAL PAYLOAD:");
      console.log("   → document_id:", payload.document_id);
      console.log("   → date:", payload.date);
      console.log("   → meal_type:", payload.meal_type);
      console.log("   → food_items count:", payload.food_items.length);
      console.log("   → Full payload:", payload);

      // Call save meal API
      const response = await foodApiService.saveMeal(payload);

      // Show success message
      // message.success({
      //   content:
      //     response.data.message ||
      //     `Successfully saved ${foodItems.length} food item${foodItems.length !== 1 ? "s" : ""}!`,
      //   duration: 3,
      // });

      // Clear localStorage after successful save
      localStorage.removeItem("glucera_food_items");
      localStorage.removeItem("glucera_uploaded_image");
      localStorage.removeItem("glucera_document_id"); // Clear document ID after successful save
      localStorage.removeItem("glucera_meal_date"); // Clear meal date after successful save
      localStorage.removeItem("glucera_analyze_response"); // Clear full API response after successful save

      // Update food items with returned food_item_ids from API
      if (response.data.food_items) {
        const updatedItems = response.data.food_items.map((apiItem, index) => ({
          id: index + 1,
          food_item_id: apiItem.food_item_id,
          name: apiItem.name,
          calories: apiItem.calorie_content_kcal,
          weight: apiItem.serving_size_g,
          servings: 1,
          image: currentUploadedImage ?? uploadedImage ?? ImageDosa,
          carbohydrate_content_g: apiItem.carbohydrate_content_g,
          protein_content_g: apiItem.protein_content_g,
          fat_content_g: apiItem.fat_content_g,
          dietary_fiber_g: apiItem.dietary_fiber_g,
          net_carbs_g: apiItem.net_carbs_g,
          sugar_g: apiItem.sugar_g,
          trans_fat_g: apiItem.trans_fat_g,
          saturated_fat_g: apiItem.saturated_fat_g,
          GI_estimate: apiItem.GI_estimate,
          GL_estimate: apiItem.GL_estimate,
          serving_description: apiItem.serving_description,
        }));

        setFoodItems(updatedItems);
      }

      // Redirect to Meal page after a short delay
      setTimeout(() => {
        setIsSaving(false);
        navigate(`/glucera${GluceraNavigationRoutes.Mealpage}`, {
          state: {
            message: "Food items saved successfully!",
            fromSave: true,
            savedItemsCount: foodItems.length,
            savedMealId: response.data.meal_id,
          },
        });
      }, 2000); // 2 second delay to show success message
    } catch (error: any) {
      setIsSaving(false);

      // Safe error message extraction
      let errorMessage = "Failed to save meal. Please try again.";

      try {
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.response?.data) {
          errorMessage =
            typeof error.response.data === "string"
              ? error.response.data
              : JSON.stringify(error.response.data);
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.response?.statusText) {
          errorMessage = `API Error: ${error.response.statusText}`;
        }
      } catch (_parseError) {
        errorMessage = "Failed to save meal. Please try again.";
      }

      // Show error message
      message.error({
        content: errorMessage,
        duration: 4,
      });
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage:
            currentUploadedImage || uploadedImage
              ? `url(${currentUploadedImage || uploadedImage})`
              : `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={handleBackNavigation}
            className="rounded-xl bg-card/80 p-2 shadow-sm backdrop-blur-sm hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-xl font-bold text-foreground">Review Meal</h1>
            {fromHomePage && (
              <p className="text-xs text-muted-foreground">Meal {mealNumber}</p>
            )}
          </div>
          <div className="w-9" />
        </div>

        <div className="space-y-3">
          {/* Food Image or No Image Message */}
          {(() => {
            const imageToShow = currentUploadedImage ?? uploadedImage;

            if (imageToShow) {
              return (
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                  <div className="relative">
                    <img
                      src={imageToShow}
                      alt="Food"
                      className="h-52 w-full object-cover"
                      onError={_e => {}}
                    />
                    <div className="absolute right-2 top-2">
                      <div className="rounded-lg bg-card/90 px-2 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur-sm">
                        {foodItems.length} items detected
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                <div className="relative">
                  <div className="flex h-52 items-center justify-center bg-muted">
                    <Camera className="h-16 w-16 text-muted-foreground" />
                  </div>
                  <div className="absolute right-2 top-2">
                    <div className="rounded-lg bg-card/90 px-2 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur-sm">
                      {foodItems.length} items detected
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Total Nutrition Facts */}
          <NutritionFactsCard
            nutritionTotals={nutritionTotals}
            foodItems={foodItems}
          />

          {/* Food Items List */}
          <FoodItemsList
            foodItems={foodItems}
            uploadedImage={currentUploadedImage ?? uploadedImage}
            expandedItems={expandedItems}
            onToggleExpanded={toggleExpanded}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onAddItem={handleAddItem}
          />

          {/* Save Meal Section */}
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-lg">
            <div className="space-y-2">
              <label className="text-sm font-bold text-foreground">
                Save as
              </label>
              <select
                className="w-full rounded-xl border border-border bg-background p-3 text-foreground focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={mealType}
                onChange={e => setMealType(e.target.value)}
              >
                <option value="Breakfast">Breakfast</option>
                <option value="Lunch">Lunch</option>
                <option value="Snack">Snack</option>
                <option value="Dinner">Dinner</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <button
              onClick={isSaving ? undefined : handleSave}
              disabled={isSaving}
              className="w-full rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 font-semibold text-white transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none"
            >
              {isSaving ? "Saving..." : "Save Meal"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScannedFooddetailPage;
